import React, { forwardRef, useRef, type CSSProperties, useLayoutEffect } from 'react';
import cn from 'classnames';
import { mergeRefs } from 'react-advanced-cropper';

interface Props {
  src?: string;
  className?: string;
  crossOrigin?: 'anonymous' | 'use-credentials' | boolean;
  brightness?: number;
  saturation?: number;
  hue?: number;
  contrast?: number;
  style?: CSSProperties;
  onCanvasReady?: (canvas: HTMLCanvasElement, coords: any) => void;
  onPreviewReady?: (canvas: HTMLCanvasElement) => void;
}

export const AdjustableImage = forwardRef<HTMLCanvasElement, Props>((
  { src, className, crossOrigin, brightness = 0, saturation = 0, hue = 0, contrast = 0, style, onCanvasReady, onPreviewReady }: Props,
  ref
) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const drawImage = () => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (canvas && image && image.complete) {
      const ctx = canvas.getContext('2d');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;

      if (ctx) {
        ctx.filter = [
          `brightness(${100 + brightness}%)`,
          `contrast(${100 + contrast}%)`,
          `saturate(${100 + saturation}%)`,
          `hue-rotate(${hue * 3.6}deg)`,
        ].join(' ');

        ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);

        requestAnimationFrame(() => {
          if (canvas.width > 0 && canvas.height > 0) {
            const coords = {
              left: ((canvas.width - canvas.height) / 2),
              top: 0,
              width: canvas?.height,
              height: canvas?.height,
            };
            onCanvasReady?.(canvas, coords);
            onPreviewReady?.(canvas);
          }
        });
      }
    }
  };

    useLayoutEffect(() => {
      drawImage();
    }, [src, brightness, saturation, hue, contrast]);

    return (
      <>
        <canvas
          key={`${src}-canvas`}
          ref={mergeRefs([ref, canvasRef])}
          className={cn('adjustable-image-element', className)}
          style={style}
        />
        {src ? (
          <img
            key={`${src}-img`}
            ref={imageRef}
            className={'adjustable-image-source'}
            src={src}
            crossOrigin={crossOrigin === true ? 'anonymous' : crossOrigin || undefined}
            onLoad={drawImage}
          />
        ) : null}
      </>
    );
  },
);

AdjustableImage.displayName = 'AdjustableImage';
